import React, { useMemo, useRef, useState } from "react";
import logoIcon from "../../assets/logo.png";
import wholesumWordmark from "../../assets/wholesum.svg";
import indianFast from "../../assets/indianFast.png";
import basketImg from "../../assets/baskit.png";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../../services/auth.service";
import {
  clearAuthFlow,
  setAuthFlow,
  clearAuthSession,
  setAuthSession,
  getDashboardAccess,
} from "./components/authStore";
import AuthPopup from "./components/AuthPopup";

function routeFromNextAction(nextAction) {
  switch (String(nextAction || "").toUpperCase()) {
    case "CHANGE_PASSWORD":
    case "CHANGE_PASSWORD_REQUIRED":
      return "/admin/change-password";

    case "ONBOARDING":
    case "ONBOARDING_REQUIRED":
    case "ONBOARDING_REJECTED":
    case "REJECTED":
      return "/admin/onboarding";

    case "WAITING_APPROVAL":
    case "WAITING_FOR_ADMIN_APPROVAL":
      return "/admin/waiting-approval";

    default:
      return "/admin/dashboard";
  }
}

export default function Login() {
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onClose: null,
  });

  const showPopup = ({
    type = "error",
    title = "ALERT",
    message,
    onClose = null,
  }) => {
    setPopup({
      open: true,
      type,
      title,
      message,
      onClose,
    });
  };

  const closePopup = () => {
    const cb = popup.onClose;

    setPopup((prev) => ({
      ...prev,
      open: false,
    }));

    if (cb) cb();
  };
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  // ✅ prevent double submit
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  // input look (same as your current theme)
  const inputBase =
    "w-full h-[46px] rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[14px] text-slate-700 placeholder-slate-400 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]";

  // button width should match inputs (so keep w-full inside form box)
  const buttonBase =
    "w-full h-[46px] rounded-[10px] bg-[#d86a78] text-white font-semibold shadow-[0_12px_26px_rgba(216,106,120,0.22)] hover:bg-[#c95b69] active:scale-[0.99] transition disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100";

  const brandBlock = useMemo(
    () => (
      <div className="flex w-full max-w-[520px] flex-col items-center text-center">
        {/* logo circle */}
        <div className="mb-6 grid h-[150px] w-[150px] place-items-center rounded-full bg-[#f7fbfb]">
          <img
            src={logoIcon}
            alt="WholeSum icon"
            className="h-[170px] w-[170px] object-contain select-none"
            draggable={false}
          />
        </div>

        {/* wordmark */}
        <img
          src={wholesumWordmark}
          alt="WholeSum"
          className="h-[105px] w-auto select-none"
          draggable={false}
        />

        {/* ✅ pink underline like reference */}
        <div className="mt-3 h-[2px] w-[409px] bg-[#f2c6cc]" />

        <div className="mt-8 flex items-center justify-center gap-3">
          <img
            src={indianFast}
            alt="India's Fastest Grocery Delivery App"
            className="h-20 w-auto select-none"
            draggable={false}
          />

          {/* ✅ basket image (your final positioning) */}
          <img
            src={basketImg}
            alt="Basket"
            className="h-11 w-11 -translate-y-3 rotate-[20deg] select-none -translate-x-[84px]"
            draggable={false}
          />
        </div>
      </div>
    ),
    []
  );

  const onSubmit = async (e) => {
    e.preventDefault();

    // ✅ hard guard against double click / enter spam
    if (loading || inFlightRef.current) return;

    // basic validation (optional but helpful)
    if (!email.trim() || !password) {
      showPopup({
  type: "error",
  title: "Missing Details",
  message: "Please enter email and password",
});
      return;
    }

    inFlightRef.current = true;
    setLoading(true);

    try {
      clearAuthSession();
      clearAuthFlow();

      const res = await AuthService.loginSendOtp({ email, password });

      if (res?.directLogin && res?.accessToken) {
        const dashboardAccess = res?.dashboardAccess || {};
        const warehouseAccess = res?.warehouseAccess || {};

        const canOpenAdmin = !!dashboardAccess?.canOpenAdminDashboard;
        const canOpenWarehouse = !!dashboardAccess?.canOpenWarehouseDashboard;

        const finalSelectedWarehouseId =
          res?.selectedWarehouseId || warehouseAccess?.assignedWarehouseId || null;

        const finalCurrentDashboard =
          res?.currentDashboard || (!canOpenAdmin && canOpenWarehouse ? "warehouse" : "admin");

        setAuthSession({
          accessToken: res.accessToken,
          admin: res?.admin,
          dashboardAccess,
          warehouseAccess,
          currentDashboard: finalCurrentDashboard,
          selectedWarehouseId: finalSelectedWarehouseId,
        });

        clearAuthFlow();

        const access = getDashboardAccess();
        if (access?.canOpenAdminDashboard && access?.canOpenWarehouseDashboard) {
          nav("/admin/select-dashboard", { replace: true });
        } else {
          nav(routeFromNextAction(res?.nextAction), { replace: true });
        }

        return;
      }

      // ✅ keep only non-sensitive in localStorage
      setAuthFlow({ resetEmail: email, otpVerified: false, mode: "login" });

      // ✅ pass password via route state (not localStorage)
      nav("/admin/otp", { state: { loginPassword: password } });
    } catch (err) {
      showPopup({
  type: "error",
  title: "Login Failed",
  message: err?.message || "Login failed",
});
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto grid min-h-screen w-full max-w-[1180px] grid-cols-1 items-center gap-10 px-6 py-10 lg:grid-cols-2">
        {/* LEFT BRAND */}
        <div className="flex items-center justify-center">{brandBlock}</div>

        {/* RIGHT LOGIN */}
        <div className="flex items-center justify-center lg:justify-end">
          <div className="w-full max-w-[520px]">
            <h1 className="mb-7 text-center text-[32px] font-bold tracking-wide text-[#d86a78]">
              ADMIN LOGIN
            </h1>

            {/* ✅ form box controls the input/button width like reference */}
            <div className="mx-auto w-full max-w-[360px]">
              <form onSubmit={onSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold tracking-wider text-slate-500">
                    EMAIL
                  </label>
                  <input
                    className={inputBase}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[12px] font-semibold tracking-wider text-slate-500">
                    Password
                  </label>

                  <div className="relative">
                    <input
                      className={inputBase + " pr-12"}
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      disabled={loading}
                    />

                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      disabled={loading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-60 disabled:hover:bg-transparent"
                      aria-label={show ? "Hide password" : "Show password"}
                    >
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => nav("/admin/forgot-password")}
                      disabled={loading}
                      className="text-xs font-medium text-slate-400 hover:text-[#d86a78] disabled:opacity-60 disabled:hover:text-slate-400"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {/* ✅ button now matches input width + blocks double submit */}
                <button type="submit" className={buttonBase} disabled={loading}>
                  {loading ? "PLEASE WAIT..." : "SUBMIT"}
                </button>
              </form>

              {/* optional: small helper text */}
              {loading ? (
                <div className="mt-3 text-center text-[11px] text-slate-400">
                  Please wait… don’t press submit again.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <AuthPopup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}